import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MdSearch, MdAdd } from 'react-icons/md';
import { toast } from 'react-toastify';
import {
    fetchManufacturerProducts,
    createManufacturerProduct
} from '../../../../services/Http/ManufacturerApi';
import ProductCreateModal from './ProductCreateModal';
import { useCurrency } from '../../../../context/CurrencyProvider';

const ProductCatalog = () => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const navigate = useNavigate();
    const [records, setRecords] = useState([]);
    const [meta, setMeta] = useState({ total: 0, per_page: 10, current_page: 1 });
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);

    const loadProducts = useCallback(async (page = 1, perPage = meta.per_page, search = searchTerm) => {
        setLoading(true);
        try {
            const response = await fetchManufacturerProducts({
                page,
                perPage,
                search,
            });
            setRecords(response.data || []);
            setMeta(response.meta || { total: 0, per_page: perPage, current_page: page });
        } catch (error) {
            toast.error(t('ManufacturerProducts.errors.fetchFailed'));
        } finally {
            setLoading(false);
        }
    }, [meta.per_page, searchTerm, t]);

    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    const handleSearch = (e) => {
        e.preventDefault();
        loadProducts(1, meta.per_page, searchTerm);
    };

    const handlePageChange = (page) => {
        loadProducts(page, meta.per_page);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        setMeta((prev) => ({ ...prev, per_page: newPerPage }));
        loadProducts(page, newPerPage);
    };

    const handleCreate = async (payload) => {
        await createManufacturerProduct(payload);
        toast.success(t('ManufacturerProducts.notifications.created'));
        setShowModal(false);
        loadProducts();
    };

    const columns = [
        {
            name: t('ManufacturerProducts.table.columns.product'),
            selector: (row) => row.product_name,
            sortable: true,
        },
        {
            name: t('ManufacturerProducts.table.columns.sku'),
            selector: (row) => row.model_number,
            sortable: true,
        },
        {
            name: t('ManufacturerProducts.table.columns.sellIn'),
            selector: (row) => row.sell_in_quantity || 0,
            sortable: true,
        },
        {
            name: t('ManufacturerProducts.table.columns.sellThrough'),
            selector: (row) => row.sell_through_quantity || 0,
            sortable: true,
        },
        {
            name: t('ManufacturerProducts.table.columns.sellThroughValue'),
            selector: (row) => formatResolvedAmount({
                eurAmount: row.sell_through_value,
                localAmount: row.sell_through_value_local ?? row.sell_through_local,
                localCurrencyId: row.sell_through_currency_id ?? row.currency_id,
                localCurrencyCode: row.sell_through_currency_code ?? row.currency_code,
            }),
            sortable: true,
        },
        {
            name: t('ManufacturerProducts.table.columns.actions'),
            cell: (row) => (
                <ActionButton onClick={() => navigate(`/products/${row.id}`)}>
                    {t('ManufacturerProducts.table.view')}
                </ActionButton>
            ),
        },
    ];

    return (
        <>
            <Toolbar>
                <SearchForm onSubmit={handleSearch}>
                    <MdSearch />
                    <input
                        type="text"
                        placeholder={t('ManufacturerProducts.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </SearchForm>
                <AddButton onClick={() => setShowModal(true)}>
                    <MdAdd size={20} /> {t('ManufacturerProducts.newProduct')}
                </AddButton>
            </Toolbar>

            <DataTable
                columns={columns}
                data={records}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={meta.total}
                paginationPerPage={meta.per_page}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                highlightOnHover
                persistTableHead
            />

            <ProductCreateModal
                show={showModal}
                onClose={() => setShowModal(false)}
                onSubmit={handleCreate}
            />
        </>
    );
};

export default ProductCatalog;

const Toolbar = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
`;

const SearchForm = styled.form`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: rgba(130, 22, 146, 0.05);
    border: 1px solid rgba(130, 22, 146, 0.2);
    border-radius: 8px;
    padding: 0.375rem 0.75rem;

    input {
        border: none;
        background: transparent;
        outline: none;
        width: 220px;
    }

    svg {
        color: rgba(130, 22, 146, 1);
    }

    @media (max-width: 768px) {
        width: 100%;
        input {
            width: 100%;
        }
    }
`;

const AddButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.625rem 1rem;
    background: rgba(130, 22, 146, 1);
    color: #fff;
    border: none;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
        background: rgba(130, 22, 146, 0.9);
    }
`;

const ActionButton = styled.button`
    padding: 0.375rem 0.75rem;
    border: 1px solid rgba(130, 22, 146, 0.4);
    background: transparent;
    color: rgba(130, 22, 146, 1);
    border-radius: 6px;
    cursor: pointer;

    &:hover {
        background: rgba(130, 22, 146, 0.08);
    }
`;
